import {
    afterRenderEffect,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    OnDestroy,
    OnInit,
    signal,
    untracked,
    viewChild,
} from '@angular/core';

import { UserInputMessage } from 'AgentApp/ts/interfaces/agent-message.interface';
import { AgentModelOption } from 'AgentApp/ts/interfaces/agent-provider.interface';
import { AgentTask } from 'AgentApp/ts/interfaces/agent-task.interface';
import { AngularUser } from 'Angular/interfaces/angular-user.interface';

import { AgentSessionStateService } from 'AgentApp/ts/services/agent-session-state.service';
import { AgentSidePanelStateService } from 'AgentApp/ts/services/agent-side-panel-state.service';
import { AgentTransportService } from 'AgentApp/ts/services/agent-transport.service';
import { AgentUserPreferencesService } from 'AgentApp/ts/services/agent-user-preferences.service';

import { ConfirmModalModule } from 'Angular/features/components/modals/confirm-modal/confirm-modal.module';
import { IconModule } from 'Angular/features/directives/icon/icon.module';
import { ImageModule } from 'Angular/features/directives/image/image.module';

import { AgentLoaderComponent } from 'AgentApp/ts/features/agent/components/agent-loader/agent-loader.component';
import { AgentResponseMessageComponent } from 'AgentApp/ts/features/agent/components/agent-response-message/agent-response-message.component';
import { AgentStepsComponent } from 'AgentApp/ts/features/agent/components/agent-steps/agent-steps.component';
import { AgentSuggestedPromptsComponent } from 'AgentApp/ts/features/agent/components/agent-suggested-prompts/agent-suggested-prompts.component';
import { AgentTasksComponent } from 'AgentApp/ts/features/agent/components/agent-tasks/agent-tasks.component';
import {
    AgentToolPermissionBoxComponent,
    AgentToolPermissionChoiceData,
} from 'AgentApp/ts/features/agent/components/agent-tool-permission-box/agent-tool-permission-box.component';
import { AgentUserInputComponent } from 'AgentApp/ts/features/agent/components/agent-user-input/agent-user-input.component';
import { AgentUserMessageComponent } from 'AgentApp/ts/features/agent/components/agent-user-message/agent-user-message.component';
import { ConfirmModalComponent } from 'Angular/features/components/modals/confirm-modal/confirm-modal.component';

import { PageContextClientTool } from 'AgentApp/ts/client-tools/page-context.client-tool';

const MAX_AUDIO_CANVAS_WIDTH_ATTACHED = 142;
const MAX_AUDIO_CANVAS_WIDTH_DETACHED = 431;

@Component({
    selector: 'ycn-agent-side-panel',
    imports: [
        IconModule,
        ImageModule,
        ConfirmModalModule,
        AgentUserInputComponent,
        AgentLoaderComponent,
        AgentStepsComponent,
        AgentSuggestedPromptsComponent,
        AgentUserMessageComponent,
        AgentResponseMessageComponent,
        AgentTasksComponent,
        AgentToolPermissionBoxComponent,
    ],
    templateUrl: './agent-side-panel.component.html',
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            height: 100%;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentSidePanelComponent implements OnInit, OnDestroy {
    private readonly _connection = inject(AgentTransportService);

    private readonly _panelState = inject(AgentSidePanelStateService);

    private readonly _sessionState = inject(AgentSessionStateService);

    private readonly _preferencesService = inject(AgentUserPreferencesService);

    private readonly _pageContextService = inject(PageContextClientTool);

    protected readonly isDetached = input(false);

    protected readonly screen = this._panelState.screen;

    protected readonly pendingNewSessionMessage = this._sessionState.pendingNewSessionMessage;

    protected readonly chatState = computed(() => this._sessionState.getChatState(this._panelState.currentSessionId()));

    protected readonly currentMessages = computed(() => {
        const pending = this.pendingNewSessionMessage();
        if (pending) {
            return [{ role: 'user' as const, text: pending.text, files: pending.files }];
        }
        return this.chatState().messages;
    });

    protected readonly isLoading = computed(() => !!this.pendingNewSessionMessage() || this.chatState().isLoading);

    protected readonly currentLoadingText = computed(() => this.chatState().currentLoadingText);

    protected readonly currentTaskSteps = computed(() => this.chatState().agentSteps);

    protected readonly stepsExpanded = signal(true);

    protected readonly currentToolCallToConfirm = computed(() => this.chatState().toolCallsToConfirm[0] ?? null);

    // The icon shown when the agent is waiting for input
    protected readonly showIdleIcon = computed(() => {
        const messages = this.currentMessages();
        const { isStreaming } = this.chatState();
        const liveSteps = this.currentTaskSteps();
        const last = messages[messages.length - 1];
        return !isStreaming && !liveSteps.length && last?.role === 'assistant';
    });

    protected readonly queuedMessagesForDisplay = computed(() =>
        this.chatState().queuedMessages.map((message) => ({
            text: message.text,
            files: message.files.map((file) => ({
                attachment_url: file.name,
                attachment_mime_type: file.type,
                external_file_id: '',
            })),
        }))
    );

    private readonly _isAgentBusy = computed(() => {
        const state = this.chatState();
        return state.isStreaming || state.toolCallsToConfirm.length > 0;
    });

    protected readonly visibleTasks = this._sessionState.visibleTasks;

    protected readonly completedTasks = this._sessionState.completedTasks;

    protected readonly inProgressTasks = this._sessionState.inProgressTasks;

    protected readonly cancelledTasks = this._sessionState.cancelledTasks;

    protected readonly taskCompletionPercentage = this._sessionState.taskCompletionPercentage;

    protected readonly hasInProgressTasks = this._sessionState.hasInProgressTasks;

    protected readonly scrollContainer = viewChild<ElementRef>('scrollContainer');

    protected readonly scrollContent = viewChild<ElementRef>('scrollContent');

    protected readonly deleteChatModal = viewChild<ConfirmModalComponent>('deleteChatModal');

    protected readonly isAtBottom = signal(true);

    private readonly _isFollowingScroll = signal(true);

    protected readonly showScrollDownButton = computed(() => {
        const isChatScreen = this.isChatScreen();
        const isAtBottom = this.isAtBottom();
        const currentMessages = this.currentMessages();
        return isChatScreen && !isAtBottom && currentMessages.length > 0;
    });

    private static readonly SCROLL_BOTTOM_THRESHOLD_PX = 50;

    private static readonly AUTO_PAUSE_VIEWPORT_FRACTION = 0.75;

    private _autoPausedAtMessagesLength = 0;

    private _lastScrollTop = 0;

    protected readonly isWelcomeScreen = computed(() => {
        const sessionId = this._panelState.currentSessionId();
        const pendingNewSessionMessage = this.pendingNewSessionMessage();
        return sessionId === null && !pendingNewSessionMessage;
    });

    protected readonly isChatScreen = computed(() => {
        const sessionId = this._panelState.currentSessionId();
        const pendingNewSessionMessage = this.pendingNewSessionMessage();
        return sessionId !== null || !!pendingNewSessionMessage;
    });

    protected readonly user: AngularUser = window.USER;

    // Only populated for users allowed to pick a model; the model is tied to the session once a chat starts.
    protected readonly modelOptions = signal<AgentModelOption[]>([]);

    protected readonly selectedModelId = signal<string | null>(null);

    protected readonly maxAudioCanvasWidth = computed(() => {
        const isDetached = this.isDetached();

        if (isDetached) {
            return MAX_AUDIO_CANVAS_WIDTH_DETACHED;
        }
        return MAX_AUDIO_CANVAS_WIDTH_ATTACHED;
    });

    constructor() {
        // Cancel auto-follow mode if the user scrolls away from the bottom
        effect((onCleanup) => {
            const el = this.scrollContainer()?.nativeElement;
            if (!el) {
                return;
            }
            const onScroll = (): void => this._syncFromScroll(el);
            el.addEventListener('scroll', onScroll, { passive: true });
            onCleanup(() => el.removeEventListener('scroll', onScroll));
        });

        // Content can grow after the inserting render (like charts after their data loads). afterRenderEffect can't
        // see that, so we watch the content column's height and keep bottom detection in sync with the real
        // content height. All messages render inside #scrollContent, so it tracks late changes.
        effect((onCleanup) => {
            const el: HTMLElement | undefined = this.scrollContainer()?.nativeElement;
            const content: HTMLElement | undefined = this.scrollContent()?.nativeElement;
            if (!el || !content) {
                return;
            }
            const observer = new ResizeObserver(() => {
                if (untracked(() => this._isFollowingScroll())) {
                    el.scrollTop = el.scrollHeight;
                }
                this.isAtBottom.set(this._isElementAtBottom(el));
            });
            observer.observe(content);
            onCleanup(() => observer.disconnect());
        });

        // After every render: auto scroll when following, resync bottom detection from the new DOM,
        // and apply aut-pause threshold.
        afterRenderEffect(() => {
            this.isLoading();
            this.currentMessages();
            this.currentTaskSteps();
            this.currentToolCallToConfirm();
            this.queuedMessagesForDisplay();

            const el = this.scrollContainer()?.nativeElement;
            if (!el) {
                return;
            }
            const following = untracked(() => this._isFollowingScroll());
            if (following) {
                el.scrollTop = el.scrollHeight;
            }
            this.isAtBottom.set(this._isElementAtBottom(el));
            if (following) {
                this._applyAutoPauseThreshold(el);
            }
        });

        // Show the model the open session was created with, or the default model for a new chat
        effect(() => {
            const sessionId = this._panelState.currentSessionId();
            const modelOptions = this.modelOptions();
            untracked(() => {
                if (!modelOptions.length) {
                    return;
                }
                if (sessionId === null) {
                    this.selectedModelId.set(modelOptions[0].id);
                    return;
                }
                this._connection
                    .getSession(sessionId)
                    .then((session) => {
                        if (this._panelState.currentSessionId() === sessionId) {
                            this.selectedModelId.set(session.model);
                        }
                    })
                    .catch(() => undefined);
            });
        });

        // Reset session state on session change
        effect(() => {
            this._panelState.currentSessionId();
            this._isFollowingScroll.set(true);
            this._autoPausedAtMessagesLength = 0;
            this._lastScrollTop = 0;
        });

        effect(async () => {
            const sessionId = this._panelState.currentSessionId();
            const isAgentBusy = this._isAgentBusy();
            const { queuedMessages } = this.chatState();
            if (!sessionId || isAgentBusy) {
                return;
            }
            if (!queuedMessages.length) {
                return;
            }
            await untracked(async () => {
                const queued = this._sessionState.dequeueAllMessages(sessionId);
                if (!queued.length) {
                    return;
                }
                const smartCompareSecurityIds = [
                    ...new Set(queued.flatMap((message) => message.smartCompareSecurityIds ?? [])),
                ];
                const combined: UserInputMessage = {
                    text: queued
                        .map((message) => message.text)
                        .filter(Boolean)
                        .join('\n\n'),
                    files: queued.flatMap((message) => message.files),
                    ...(smartCompareSecurityIds.length >= 2 ? { smartCompareSecurityIds } : {}),
                };
                await this.sendMessage(combined);
            });
        });
    }

    ngOnInit(): void {
        this._preferencesService.get().subscribe(async (prefs) => {
            this.stepsExpanded.set(prefs.agent_side_panel_steps_expanded);
            this.modelOptions.set(prefs.model_options ?? []);
            const savedSessionId = prefs.last_visited_side_menu_session_id;
            if (savedSessionId != null) {
                this._panelState.currentSessionId.set(savedSessionId);
                try {
                    await this._connection.loadAndResumeSession(savedSessionId, window.USER.userId);
                } catch {
                    this._panelState.currentSessionId.set(null);
                    this._preferencesService.save({ last_visited_side_menu_session_id: null }).subscribe();
                }
            } else {
                // The panel state service outlives this component, so clear any session retained
                // from a previous panel open that the server no longer considers resumable.
                this._panelState.currentSessionId.set(null);
            }
        });
    }

    ngOnDestroy(): void {
        const sessionId = this._panelState.currentSessionId();
        if (sessionId !== null) {
            this._connection.detach(sessionId);
        }
    }

    onModelChange(modelId: string): void {
        if (!this.isChatScreen()) {
            this.selectedModelId.set(modelId);
        }
    }

    openNewChat(): void {
        // The previous chat keeps no stream or listener open once it is no longer shown.
        const previousSessionId = this._panelState.currentSessionId();
        if (previousSessionId !== null) {
            this._connection.disconnect(previousSessionId);
        }
        this._panelState.currentSessionId.set(null);
        this._preferencesService.save({ last_visited_side_menu_session_id: null }).subscribe();
    }

    async onSuggestedPromptSelected(prompt: string): Promise<void> {
        await this.sendMessage({ text: prompt, files: [] });
    }

    onStepsToggled(expanded: boolean): void {
        this._preferencesService.save({ agent_side_panel_steps_expanded: expanded }).subscribe();
    }

    openDeleteChatModal(): void {
        this.deleteChatModal()?.openModal();
    }

    onDeleteChatConfirmed(confirm: boolean): void {
        const sessionId = this._panelState.currentSessionId();
        if (!confirm || sessionId === null) {
            return;
        }
        this.openNewChat();
        this._sessionState.deleteSessionById(sessionId);
    }

    scrollToBottom(): void {
        this._isFollowingScroll.set(true);
        const el = this.scrollContainer()?.nativeElement;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    }

    private _isElementAtBottom(el: HTMLElement): boolean {
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        return distanceFromBottom <= AgentSidePanelComponent.SCROLL_BOTTOM_THRESHOLD_PX;
    }

    private _syncFromScroll(el: HTMLElement): void {
        const atBottom = this._isElementAtBottom(el);
        // check whether a user actually scrolled up to break auto followns
        const scrolledUp = el.scrollTop < this._lastScrollTop;
        this._lastScrollTop = el.scrollTop;
        this.isAtBottom.set(atBottom);
        if (!atBottom && scrolledUp) {
            this._isFollowingScroll.set(false);
        }
    }

    // Pause auto follow once the BODY of the streamed in message is too tall
    private _applyAutoPauseThreshold(el: HTMLElement): void {
        const messages = untracked(() => this.currentMessages());
        const last = messages[messages.length - 1];
        if (last?.role !== 'assistant' || !last.text || this._autoPausedAtMessagesLength === messages.length) {
            return;
        }
        const textBodies = el.querySelectorAll('ycn-agent-response-message .ai-md');
        const lastTextBody = textBodies[textBodies.length - 1];
        if (
            lastTextBody &&
            lastTextBody.getBoundingClientRect().height >
                el.clientHeight * AgentSidePanelComponent.AUTO_PAUSE_VIEWPORT_FRACTION
        ) {
            this._autoPausedAtMessagesLength = messages.length;
            this._isFollowingScroll.set(false);
        }
    }

    async sendMessage(message: UserInputMessage): Promise<void> {
        this._isFollowingScroll.set(true);
        const currentSessionId = this._panelState.currentSessionId();

        if (currentSessionId && this._isAgentBusy()) {
            this._sessionState.enqueueMessage(currentSessionId, message);
            return;
        }

        const sliceKey = await this._pageContextService.getCurrentSliceKey();
        const clientContext = { source: 'side_panel' as const, app_state_slice_key: sliceKey };
        window.pendoTrack('Submitted Input to Agent', {
            message: message.text,
            fileCount: message.files.length,
            source: clientContext.source,
        });
        if (currentSessionId) {
            await this._connection.sendUserMessage(
                currentSessionId,
                message.text,
                message.files,
                clientContext,
                message.smartCompareSecurityIds
            );
        } else {
            const selectedModel = this.modelOptions().find((option) => option.id === this.selectedModelId());
            const session = await this._connection.createSession(
                message.text,
                message.files,
                selectedModel ? { provider: selectedModel.provider, model: selectedModel.id } : {}
            );
            this._panelState.currentSessionId.set(session.id);
            this._preferencesService.save({ last_visited_side_menu_session_id: session.id }).subscribe();
            const recentSession = {
                id: session.id,
                name: session.name,
                pinned: false,
                provider: session.provider,
            };
            this._sessionState.addSessionToRecent(recentSession);
            this._connection.generateAndApplySessionDisplay(recentSession, message.text);
            await this._connection.sendUserMessage(
                session.id,
                message.text,
                message.files,
                clientContext,
                message.smartCompareSecurityIds
            );
        }
    }

    onDeleteTask(task: AgentTask): void {
        this._sessionState.deleteTask(task);
    }

    onHideTask(task: AgentTask): void {
        this._sessionState.hideTask(task);
    }

    onTaskSelected(task: AgentTask): void {
        this._sessionState.selectTask(task);
    }

    onToolPermissionConfirm(event: AgentToolPermissionChoiceData): void {
        const toolCall = this.currentToolCallToConfirm();
        const sessionId = this._panelState.currentSessionId();
        if (toolCall && sessionId) {
            this._connection.confirmToolCall(
                sessionId,
                window.USER.userId,
                event.authorize,
                event.alwaysApply,
                toolCall,
                event.reason
            );
        }
    }
}
